// Shared "who changed this cell, and when" — the same history the Overview page shows, reusable on every
// Smartsheet-backed grid (Service, GRP, PDI, DS, Deck, …). History is newest-first, so the top entry is the
// most recent change; the whole list is shown.
//   mouse — a page spreads onPointerEnter={onEnter(…)} / onPointerLeave={onLeave} onto each cell and renders
//           the returned {popover} once; hovering shows a small fixed card beside the cell, unless Settings ›
//           Cell history is off.
//   touch — the same history opens as a 'Change history' bottom sheet: a 500ms long-press on the cell
//           ({...bindPress(…)}) or the clock button pages put beside a field caption (historyButton(…)).
//           The hover card never shows for touch, so a tap on an editable cell only edits it.

import { useEffect, useRef, useState, type MouseEvent, type PointerEvent, type ReactNode, type SVGProps } from 'react';
import { createPortal } from 'react-dom';
import { useSheetCellHistory } from '../features/hooks.js';
import { useNameResolver } from '../features/directory.js';
import { useDateFormatter } from '../lib/format.js';
import { useIsCoarse } from '../lib/media.js';
import { usePrefs } from '../settings/SettingsContext.js';
import { InlineLoading, Modal } from './ui.js';

interface Target { rowId: number; columnId: number; label: string; title: string }
interface Anchor extends Target { top: number; left: number }

// the hover card's box: 264 wide, at most 300 tall (+8 clear of the window edge)
const POP_W = 264;
const POP_H = 300;

function CellHistoryPopover({ sheetId, rowId, columnId, label, title, top, left }: Anchor & { sheetId: string }) {
  const { data, isLoading, isError } = useSheetCellHistory(sheetId, rowId, columnId);
  const nameOf = useNameResolver();
  const df = useDateFormatter();
  const entries = data?.history ?? [];
  return (
    <div
      style={{
        position: 'fixed', top, left, zIndex: 60, width: POP_W, maxHeight: POP_H, overflowY: 'auto',
        background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-md)', padding: 10, pointerEvents: 'none', fontSize: 12,
      }}
    >
      <div style={{ fontWeight: 700 }}>Change history</div>
      <div className="muted" style={{ fontSize: 11, marginBottom: 8 }}>{title}{title && label ? ' · ' : ''}{label}</div>
      {isLoading && <InlineLoading label="Loading history…" />}
      {isError && <div className="muted">Couldn’t load the change history.</div>}
      {!isLoading && data && !data.available && <div className="muted">History isn’t available on this Smartsheet plan.</div>}
      {!isLoading && data?.available && entries.length === 0 && <div className="muted">No recorded changes.</div>}
      {entries.map((e, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '5px 0', borderTop: i ? '1px solid var(--border)' : 'none' }}>
          <span className="pill" style={{ flexShrink: 0, maxWidth: 96, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{df.maybe(e.value) || '-'}</span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.by || nameOf(e.email) || 'Unknown'}</div>
            <div className="muted" style={{ fontSize: 11 }}>{df.dateTime(e.at)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

/** the touch presentation: the same history in a bottom sheet you can scroll and close. Portalled to <body>
 *  so a cell inside a transformed sheet or a glass bar can't trap the fixed layer. */
function CellHistorySheet({ sheetId, rowId, columnId, label, title, onClose }: Target & { sheetId: string; onClose: () => void }) {
  const { data, isLoading, isError } = useSheetCellHistory(sheetId, rowId, columnId);
  const nameOf = useNameResolver();
  const df = useDateFormatter();
  const entries = data?.history ?? [];
  return createPortal(
    <Modal sheet title="Change history" onClose={onClose} closeLabel="Close change history">
      <div className="chist">
        <div className="chist-cap">
          {label && <span className="chist-field">{label}</span>}
          {title && <span className="chist-rec">{title}</span>}
        </div>
        {isLoading && <InlineLoading label="Loading history…" />}
        {isError && <div className="muted">Couldn’t load the change history.</div>}
        {!isLoading && data && !data.available && <div className="muted">History isn’t available on this Smartsheet plan.</div>}
        {!isLoading && data?.available && entries.length === 0 && <div className="muted">No recorded changes.</div>}
        {entries.length > 0 && (
          <ol className="chist-list">
            {entries.map((e, i) => (
              <li key={i} className="chist-row">
                <span className="pill chist-val">{df.maybe(e.value) || '-'}</span>
                <span className="chist-who">
                  <span className="chist-by">{e.by || nameOf(e.email) || 'Unknown'}</span>
                  <span className="chist-at">{df.dateTime(e.at)}</span>
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </Modal>,
    document.body,
  );
}

/** clock — change history (Feather style, like components/icons.tsx) */
function ClockIcon({ size = 16, ...rest }: { size?: number } & SVGProps<SVGSVGElement>) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ verticalAlign: 'middle', flexShrink: 0 }} {...rest}>
      <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
    </svg>
  );
}

/** the touch way into a field's history: a 32px clock beside its caption, with a 44px hit area. Pages
 *  normally render it through useCellHistoryHover's historyButton(…), which shows it on touch only. */
export function HistoryButton({ label, onClick, className }: { label: string; onClick: () => void; className?: string }) {
  return (
    <button
      type="button"
      className={`chist-btn hit-44${className ? ` ${className}` : ''}`}
      aria-label={label ? `Change history: ${label}` : 'Change history'}
      title="Change history"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
    >
      <ClockIcon size={16} />
    </button>
  );
}

/** after a long-press has opened the sheet, the lifting finger must not also press the control under it (or
 *  the sheet's own scrim, which would close it again): eat that one mousedown + click. A new gesture
 *  (the next pointerdown) or a timeout disarms it, since Android sends no click after a long-press. */
function swallowPressClick() {
  const eat = (e: Event) => { e.preventDefault(); e.stopPropagation(); if (e.type === 'click') done(); };
  const done = () => {
    document.removeEventListener('mousedown', eat, true);
    document.removeEventListener('click', eat, true);
    document.removeEventListener('pointerdown', done, true);
    window.clearTimeout(timer);
  };
  document.addEventListener('mousedown', eat, true);
  document.addEventListener('click', eat, true);
  document.addEventListener('pointerdown', done, true);
  const timer = window.setTimeout(done, 4000);
}

const LONG_PRESS_MS = 500;
const MOVE_SLOP = 8;

/** Attach cell history to a grid.
 *  - `onEnter(rowId, columnId, label, title)` returns the onPointerEnter handler for a cell (mouse only);
 *    `onLeave` is its onPointerLeave. The card clears itself when the page scrolls under it.
 *  - `bindPress(rowId, columnId, label, title)` returns the touch long-press handlers to spread on the cell.
 *  - `openFor(rowId, columnId, label, title)` opens the history sheet directly.
 *  - `historyButton(rowId, columnId, label, title)` renders a HistoryButton on touch screens, else null.
 *  - `popover` is rendered once at the end of the page (the hover card and the sheet). */
export function useCellHistoryHover(sheetId: string | null | undefined) {
  const coarse = useIsCoarse();
  const { cellHistory: hoverOn } = usePrefs(); // Settings › Display › Cell history
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const [sheet, setSheet] = useState<Target | null>(null);
  const cellRef = useRef<Element | null>(null);
  const press = useRef<{ timer: number; x: number; y: number } | null>(null);
  const lastTouch = useRef(0);

  // the card is fixed to where the cell was: once the page (or the grid around the cell) scrolls, it would
  // point at the wrong row, so it goes.
  useEffect(() => {
    if (!anchor) return;
    const onScroll = (e: Event) => {
      const t = e.target;
      const cell = cellRef.current;
      if (!cell || t === document || (t instanceof Node && t.contains(cell))) setAnchor(null);
    };
    document.addEventListener('scroll', onScroll, true);
    return () => document.removeEventListener('scroll', onScroll, true);
  }, [anchor]);

  useEffect(() => () => { if (press.current) window.clearTimeout(press.current.timer); }, []);

  const onEnter =
    (rowId: number, columnId: number, label: string, title = '') =>
    (e: PointerEvent) => {
      if (e.pointerType !== 'mouse' || !hoverOn) return;
      const r = e.currentTarget.getBoundingClientRect();
      cellRef.current = e.currentTarget;
      setAnchor({
        rowId, columnId, label, title,
        top: Math.min(r.bottom + 4, Math.max(8, window.innerHeight - POP_H - 8)),
        left: Math.min(r.left, window.innerWidth - POP_W - 20),
      });
    };
  const onLeave = () => { cellRef.current = null; setAnchor(null); };

  const openFor = (rowId: number, columnId: number, label: string, title = '') => {
    setAnchor(null);
    setSheet({ rowId, columnId, label, title });
  };

  const cancelPress = () => {
    if (press.current) { window.clearTimeout(press.current.timer); press.current = null; }
  };
  const bindPress = (rowId: number, columnId: number, label: string, title = '') => ({
    onPointerDown: (e: PointerEvent) => {
      if (e.pointerType === 'mouse' || !e.isPrimary || !sheetId) return;
      lastTouch.current = Date.now();
      cancelPress();
      const timer = window.setTimeout(() => {
        press.current = null;
        swallowPressClick();
        openFor(rowId, columnId, label, title);
      }, LONG_PRESS_MS);
      press.current = { timer, x: e.clientX, y: e.clientY };
    },
    onPointerMove: (e: PointerEvent) => {
      const p = press.current;
      if (p && Math.hypot(e.clientX - p.x, e.clientY - p.y) > MOVE_SLOP) cancelPress();
    },
    onPointerUp: cancelPress,
    // a scroll that starts on the cell hands the touch to the browser, which cancels the pointer
    onPointerCancel: cancelPress,
    // Android raises the context menu (and text selection) on a long-press; a mouse right-click is left alone
    onContextMenu: (e: MouseEvent) => { if (Date.now() - lastTouch.current < 2000) e.preventDefault(); },
    'data-hist-press': '',
  });

  const historyButton = (rowId: number, columnId: number, label: string, title = ''): ReactNode =>
    coarse && sheetId ? <HistoryButton label={label} onClick={() => openFor(rowId, columnId, label, title)} /> : null;

  const popover: ReactNode = sheetId ? (
    <>
      {anchor && hoverOn && <CellHistoryPopover sheetId={sheetId} {...anchor} />}
      {sheet && <CellHistorySheet sheetId={sheetId} {...sheet} onClose={() => setSheet(null)} />}
    </>
  ) : null;
  return { onEnter, onLeave, bindPress, openFor, historyButton, popover };
}
